/**
 * GenerationProgress — Supercool-tier "never feels stuck" progress bar for
 * every AI generation in Konnect (Magic Studio + Advanced Projects).
 *
 * Standing order (2026-07-22, user directive):
 *   "when generating, there must be a progressive percentage and texts
 *    while the generation is on going, so that the subscriber will not
 *    ever feel that his generation is stuck. Do that in all generations."
 *
 * Behaviour:
 *   - Uses `startedAt` (ISO ms) and per-kind ETA to compute a smoothly
 *     rising % client-side (min ceiling 92% until the job actually reports
 *     `complete`, then snaps to 100%).
 *   - Rotates 4 phase-specific status texts so the user sees continuous
 *     visible progress even when the poll is quiet.
 *   - Rock-solid: never overflows 100%, never regresses, no dependencies
 *     on the network beyond the initial startedAt.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

// Per-kind ETA (seconds) — empirically matches Kaharian round-trip.
// v10.3.5 Kaharian Hub — video takes 90–180s (VEO 3.1) up to 300s (Seedance);
// timeout is 360s. ETA of 180s = mid-point, produces a natural-feeling curve.
const ETA = {
  image: 30,
  video: 180,
  music: 60,
  text:  18,
  render: 90, // MP4 assembly
};

const PHASE_LABELS = {
  image: [
    'Understanding your prompt…',
    'Composing the scene…',
    'Rendering with Nano Banana 2…',
    'Polishing lighting & detail…',
    'Finalizing (large files can take a moment)…',
  ],
  video: [
    'Interpreting your brief…',
    'Storyboarding frames…',
    'Rendering motion (Kling / Veo)…',
    'Upscaling & colour-grading…',
    'Finalizing (large files can take a moment)…',
  ],
  music: [
    'Interpreting mood & tempo…',
    'Arranging melody + rhythm…',
    'Generating stems (Suno)…',
    'Mixing & mastering…',
    'Finalizing (large files can take a moment)…',
  ],
  text: [
    'Understanding your brief…',
    'Drafting outline…',
    'Writing your copy…',
    'Polishing tone & clarity…',
    'Finalizing…',
  ],
  render: [
    'Loading scene assets…',
    'Stitching timeline…',
    'Encoding video (ffmpeg)…',
    'Muxing audio + captions…',
    'Finalizing MP4 (large files can take a moment)…',
  ],
};

/**
 * @param {Object} props
 * @param {string} props.kind — 'image' | 'video' | 'music' | 'text' | 'render'
 * @param {string|number|Date} [props.startedAt] — when the job kicked off
 * @param {boolean} [props.complete] — if true, snaps to 100%
 * @param {boolean} [props.failed] — if true, hides bar (error UI takes over)
 * @param {string} [props.className]
 * @param {'compact'|'full'} [props.variant='full']
 */
export default function GenerationProgress({
  kind = 'image',
  startedAt,
  complete = false,
  failed = false,
  className = '',
  variant = 'full',
}) {
  const startMs = useMemo(() => {
    if (!startedAt) return Date.now();
    try {
      const t = new Date(startedAt).getTime();
      return Number.isFinite(t) ? t : Date.now();
    } catch { return Date.now(); }
  }, [startedAt]);

  const eta = ETA[kind] || 30;
  const labels = PHASE_LABELS[kind] || PHASE_LABELS.image;

  const [pct, setPct] = useState(0);
  const [phaseIdx, setPhaseIdx] = useState(0);

  useEffect(() => {
    if (failed) return;
    if (complete) { setPct(100); setPhaseIdx(labels.length - 1); return; }
    const iv = setInterval(() => {
      const elapsed = (Date.now() - startMs) / 1000;
      // easeOut curve so first 5s doesn't look empty and last 30% doesn't stall
      const raw = 100 * (1 - Math.exp(-elapsed / (eta * 0.55)));
      const clamped = Math.min(92, Math.max(1, raw));
      setPct(clamped);
      const pIdx = Math.min(labels.length - 1, Math.floor((clamped / 92) * labels.length));
      setPhaseIdx(pIdx);
    }, 400);
    return () => clearInterval(iv);
  }, [complete, failed, startMs, eta, labels.length]);

  if (failed) return null;

  const shown = complete ? 100 : pct;
  const label = complete ? 'Ready' : labels[phaseIdx];

  if (variant === 'compact') {
    return (
      <div className={`w-full ${className}`}>
        <div className="flex items-center justify-between text-[10px] text-slate-600 dark:text-white/70 mb-1">
          <span className="truncate">{label}</span>
          <span className="tabular-nums text-emerald-600 dark:text-emerald-300 font-semibold">{Math.round(shown)}%</span>
        </div>
        <div className="h-1 w-full rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 dark:from-emerald-400 dark:to-cyan-400 transition-all duration-500 ease-out"
            style={{ width: `${shown}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`text-center px-4 w-full ${className}`}>
      <Loader2 className="h-6 w-6 animate-spin text-emerald-400 mx-auto mb-2" />
      <div className="flex items-center justify-between text-[11px] text-white/80 mb-1 max-w-[260px] mx-auto">
        <span className="truncate mr-2">{label}</span>
        <span className="tabular-nums text-emerald-300 font-semibold">{Math.round(shown)}%</span>
      </div>
      <div className="h-1.5 w-full max-w-[260px] mx-auto rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-sky-400 transition-all duration-500 ease-out"
          style={{ width: `${shown}%` }}
        />
      </div>
      <div className="text-[10px] text-white/40 mt-2">Streaming from Kaharian Gateway · you can leave this tab open</div>
    </div>
  );
}
